import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShoppingBag, Check } from "lucide-react";
import { api } from "@/lib/api";
import PublicLayout, { PrimaryBtn } from "@/components/public/PublicLayout";
import { ROUTES, C, SERIF } from "@/lib/publicLinks";
import { useCart, imgUrl } from "@/context/CartContext";

export default function ProductDetail() {
  const { slug } = useParams();
  const { add } = useCart();
  const [p, setP] = useState(undefined); // undefined=loading, null=not found
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/shop/products/${slug}`); setP(data.product); }
      catch { setP(null); }
    })();
  }, [slug]);

  if (p === undefined) return <PublicLayout><div className="max-w-5xl mx-auto px-6 py-20 text-sm text-slate-500">Loading…</div></PublicLayout>;
  if (p === null) return (
    <PublicLayout>
      <div className="max-w-5xl mx-auto px-6 py-20 text-center">
        <p className="text-slate-700" style={SERIF}>Product not found.</p>
        <div className="mt-6 flex justify-center"><PrimaryBtn to={ROUTES.shop}>Back to Shop</PrimaryBtn></div>
      </div>
    </PublicLayout>
  );

  const sale = p.sale_price && p.sale_price > 0;
  const now = sale ? p.sale_price : p.price;
  const soldOut = (p.track_inventory && p.stock_qty <= 0) || p.in_stock === false;
  const imgs = p.images && p.images.length ? p.images : [];
  const onAdd = () => { add(p, qty); setAdded(true); setTimeout(() => setAdded(false), 1500); };

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to={ROUTES.shop} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-6"><ArrowLeft className="h-4 w-4" /> Back to Shop</Link>
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <div className="rounded-2xl overflow-hidden bg-slate-100" style={{ aspectRatio: "1/1", border: `1px solid ${C.line}` }} data-testid="product-detail-image">
              {imgs.length
                ? <img src={imgUrl(imgs[activeImg])} alt={p.name} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-slate-300"><ShoppingBag className="h-14 w-14" /></div>}
            </div>
            {imgs.length > 1 && (
              <div className="mt-3 flex gap-2">
                {imgs.map((im, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className="h-16 w-16 rounded-lg overflow-hidden" style={{ border: i === activeImg ? `2px solid ${C.primary}` : `1px solid ${C.line}` }}>
                    <img src={imgUrl(im)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            {p.category ? <div className="text-xs uppercase tracking-wider" style={{ color: C.accent }}>{p.category}</div> : null}
            <h1 className="mt-1 text-3xl sm:text-4xl text-slate-900" style={SERIF} data-testid="product-detail-name">{p.name}</h1>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-2xl font-semibold text-slate-900">${Number(now).toFixed(2)}</span>
              {sale ? <span className="text-lg text-slate-400 line-through">${Number(p.price).toFixed(2)}</span> : null}
            </div>
            <p className="mt-5 text-slate-600 leading-relaxed">{p.description || p.short_description}</p>
            {p.usage_instructions ? (
              <div className="mt-5 rounded-xl p-4" style={{ background: C.lightBg }}>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">How to Use</div>
                <p className="text-sm text-slate-700">{p.usage_instructions}</p>
              </div>
            ) : null}

            {!soldOut ? (
              <div className="mt-7 flex items-center gap-4">
                <div className="inline-flex items-center rounded-full border" style={{ borderColor: C.line }}>
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2.5" data-testid="qty-decrease"><Minus className="h-4 w-4" /></button>
                  <span className="px-4 text-sm font-semibold" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="px-3 py-2.5" data-testid="qty-increase"><Plus className="h-4 w-4" /></button>
                </div>
                <button onClick={onAdd} className="flex-1 rounded-full px-6 py-3 text-sm font-semibold text-white inline-flex items-center justify-center gap-2"
                  style={{ background: added ? C.green : C.primary }} data-testid="product-add-to-cart">
                  {added ? <><Check className="h-4 w-4" /> Added to Cart</> : "Add to Cart"}
                </button>
              </div>
            ) : (
              <div className="mt-7"><button disabled className="rounded-full px-6 py-3 text-sm font-semibold bg-slate-100 text-slate-400">Sold Out</button></div>
            )}

            <div className="mt-6 flex gap-3">
              <PrimaryBtn to={ROUTES.cart} className="!bg-white !text-[#4A1D5E]" style={{ border: `1px solid ${C.line}` }}>View Cart</PrimaryBtn>
            </div>

            <p className="mt-8 text-xs text-slate-400 leading-relaxed">These statements have not been evaluated to diagnose, treat, cure, or prevent any disease. KISSED products are intended to support general wellness. Consult a licensed medical provider for medical concerns.</p>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
